import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { AuthShell } from "../auth/components/AuthShell";
import { CreateOrganizationWizard } from "../iam/organization/components/CreateOrganizationWizard";
import { WorkspaceReadyGate } from "../onboarding/components/WorkspaceReadyGate";

/**
 * organizationsNewSearchSchema validates the /organizations/new search params. The
 * onboarding flag is set only when the _app guard routes a fresh registrant here, which
 * selects the post-signup dismiss affordances (a skip link + the provisioning gate);
 * its absence selects the in-app create affordance (a modal-style close, no gate).
 */
const organizationsNewSearchSchema = z.object({
  onboarding: z.boolean().optional(),
});

/**
 * Route is /organizations/new: the create-organization page. It nests under the pathless
 * _onboarding layout (the full-screen, sidebar-free authed frame) rather than the _app
 * shell, so creating an organization takes over the whole viewport. One URL serves two
 * contexts, keyed off the onboarding search flag:
 *
 * - Post-signup onboarding (onboarding set): a fresh registrant has a live session but a
 *   not-yet-provisioned backend record, so a WorkspaceReadyGate shows a brief "setting up"
 *   state and only mounts the page once provisioning lands (invisible for an
 *   already-provisioned user on a warm cache). The page offers a bottom skip link that
 *   leaves for the Studio.
 * - In-app create (onboarding absent): an existing user opening create from the
 *   org-switcher needs no provisioning gate and no skip; instead a modal-style close
 *   returns them to where they came from.
 *
 * The wizard itself collects the details, the plan and the card, creates the organization
 * once all three are done, and closes on the screen that offers the next actions. It takes
 * the "fill" layout because it is a layout in its own right: a header and an action row
 * pinned to the panel's edges with a scrolling body between them, which a centred container
 * could neither span nor hold still. It carries its own readable measure inside those rows.
 */
export const Route = createFileRoute("/_onboarding/organizations/new")({
  component: CreateOrganizationRoute,
  validateSearch: organizationsNewSearchSchema,
});

function CreateOrganizationRoute() {
  const { onboarding } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  // The onboarding skip leaves for the Studio (a fresh registrant has no prior history to
  // return to).
  const skip = useCallback(() => {
    void navigate({ to: "/studio" });
  }, [navigate]);

  // The in-app close returns to where the user came from when there is history to pop
  // (the account/org page the org-switcher opened create from), and falls back to the
  // Studio for a direct landing with nothing to go back to.
  const close = useCallback(() => {
    if (canGoBack) {
      router.history.back();

      return;
    }

    void navigate({ to: "/studio" });
  }, [canGoBack, navigate, router]);

  // Post-signup onboarding offers an in-form skip link (no page-corner close — there is
  // nowhere to return to), behind the provisioning gate. In-app create offers the
  // page-corner close instead, threaded into the shell.
  if (onboarding) {
    return (
      <AuthShell layout="fill">
        <WorkspaceReadyGate onSkip={skip}>
          <CreateOrganizationWizard onSkip={skip} />
        </WorkspaceReadyGate>
      </AuthShell>
    );
  }

  return (
    <AuthShell layout="fill" onClose={close}>
      <CreateOrganizationWizard />
    </AuthShell>
  );
}
